import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { colors, typography } from '../styles/theme';

/** Caja del aviso: fondo y borde teal suaves (el de «Enviar consulta» para un tercero). */
export const infoNoticeBoxSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  pl: 1.25,
  pr: 1.25,
  py: 1,
  borderRadius: '8px',
  bgcolor: alpha(colors.primary, 0.12),
  border: `1px solid ${alpha(colors.primary, 0.28)}`,
};

export const infoNoticeIconSx = { fontSize: 18, color: colors.primary, mt: '1px', flexShrink: 0 };

export const infoNoticeTitleSx = {
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: 1.35,
  color: colors.textPanelTitle,
  fontFamily: typography.fontFamily.primary,
};

export const infoNoticeDescriptionSx = {
  mt: 0.25,
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: 1.4,
  color: colors.textSecondary,
  fontFamily: typography.fontFamily.primary,
};

/** Acción de texto del aviso (teal claro, legible sobre el fondo teal). */
export const infoNoticeActionSx = {
  alignSelf: 'center',
  flexShrink: 0,
  ml: 'auto',
  minWidth: 0,
  px: 1,
  // Mismo alto de línea que el título y margen negativo: no agranda la caja.
  py: '2px',
  my: '-2px',
  lineHeight: 1.35,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  fontWeight: 600,
  color: colors.primaryLight,
  '&:hover': { bgcolor: alpha(colors.primary, 0.16), color: '#FFFFFF' },
  '&.Mui-disabled': { color: alpha(colors.primaryLight, 0.45) },
};

/**
 * Aviso informativo de Terrio: ícono info teal, título en negrita y una línea
 * secundaria opcional, con una acción de texto opcional a la derecha.
 * Lo usan el form «Enviar consulta» (consulta para un tercero) y el lead
 * duplicado en alta / edición / consulta manual.
 *
 * @param {{ title: React.ReactNode, description?: React.ReactNode,
 *   actionLabel?: string, onAction?: () => void, actionDisabled?: boolean,
 *   role?: string, 'data-testid'?: string, sx?: object }} props
 */
export default function InfoNotice({
  title,
  description = null,
  actionLabel = '',
  onAction,
  actionDisabled = false,
  role = 'note',
  sx,
  'data-testid': testId,
}) {
  // Una línea (sin descripción): ícono, título y acción centrados en la caja.
  // Dos líneas: ícono alineado con el título (como siempre).
  const singleLine = !description;
  const boxSx = singleLine ? { ...infoNoticeBoxSx, alignItems: 'center' } : infoNoticeBoxSx;
  return (
    <Box data-testid={testId} role={role} sx={sx ? { ...boxSx, ...sx } : boxSx}>
      <InfoOutlinedIcon sx={singleLine ? { ...infoNoticeIconSx, mt: 0 } : infoNoticeIconSx} aria-hidden />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={infoNoticeTitleSx}>{title}</Typography>
        {singleLine ? null : <Typography sx={infoNoticeDescriptionSx}>{description}</Typography>}
      </Box>
      {actionLabel && onAction ? (
        <Button size="small" onClick={onAction} disabled={actionDisabled} sx={infoNoticeActionSx}>
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
}
